import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { authClient, setActiveOrganization } from '@/lib/auth-client';
import { Loader } from '@/components/ui/loader';

const searchSchema = z.object({
  id: z.string().optional(),
});

export const Route = createFileRoute('/_auth/accept-invitation')({
  validateSearch: searchSchema,
  pendingComponent: () => (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="mx-auto max-w-md space-y-4 text-center">
        <Loader />
        <h1 className="text-2xl font-bold">Accepting Invitation</h1>
        <p className="text-muted-foreground">Please wait...</p>
      </div>
    </div>
  ),
  beforeLoad: async ({ context, search }) => {
    if (!search.id) {
      return { acceptError: 'No invitation ID provided.' };
    }

    const { data, error } = await authClient.organization.acceptInvitation({
      invitationId: search.id,
    });

    if (error) {
      return {
        acceptError: error.message || 'Failed to accept invitation.',
      };
    }

    // Set the accepted org as active so the session has an organizationId.
    // acceptInvitation returns the member record with organizationId.
    const orgId =
      data?.invitation?.organizationId ?? data?.member?.organizationId;
    if (orgId) {
      await setActiveOrganization(orgId, context.queryClient);
    }

    throw redirect({ to: '/' });
  },
  component: AcceptInvitation,
});

function AcceptInvitation() {
  const { acceptError } = Route.useRouteContext();

  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="mx-auto max-w-md space-y-4 text-center">
        <h1 className="text-2xl font-bold">Something went wrong</h1>
        <p className="text-muted-foreground">
          {(acceptError as string) || 'Failed to accept invitation.'}
        </p>
        <p className="text-sm text-muted-foreground">
          The invitation may have expired or already been used.
        </p>
      </div>
    </div>
  );
}
